import Component from "@glimmer/component";
import { service } from "@ember/service";
import { Task, task } from "ember-concurrency";

import { arg, forbidExtraArgs } from "glesys-controlpanel/utils/arg";
import { shape, instanceOf } from "prop-types";
import { stringable } from "glesys-controlpanel/utils/prop-types";

import { tracked } from "@glimmer/tracking";

import GModalHeader from "glesys-controlpanel/components/g-modal/header";
import GModalBody from "glesys-controlpanel/components/g-modal/body";
import GModalFooter from "glesys-controlpanel/components/g-modal/footer";

import TaskForm from "glesys-controlpanel/components/task-form";
import Label from "glesys-controlpanel/components/label";
import Select from "glesys-controlpanel/components/select";
import CostCompact from "glesys-controlpanel/components/cost/compact";
import GButton from "glesys-controlpanel/components/g-button";
import InfoPopup from "glesys-controlpanel/components/info-popup";
import AlertMessage from "glesys-controlpanel/components/alert-message";
import modalClose from "glesys-controlpanel/helpers/modal-close";
import { on } from "@ember/modifier";

import { t } from "ember-intl";

@forbidExtraArgs
export default class ModalsDedicatedServerOrderForm extends Component {
  @service modal;
  @service current;
  @service abilities;

  @arg(
    shape({
      package: shape({
        name: stringable.isRequired,
      }).isRequired,
      task: instanceOf(Task).isRequired,
    }).isRequired,
  )
  params;

  selectedDataCenter = this.availableDataCenters[0]?.value;
  @tracked selectedOperatingSystem = this.availableOperatingSystem[0]?.value;
  @tracked selectedStorage = this.availableStorage[0];
  @tracked selectedConnection = this.availableConnection[0];

  get package() {
    return this.params.package;
  }

  get task() {
    return this.params.task;
  }

  get cost() {
    return this.package.mrc + this.selectedStorage?.cost + this.selectedConnection?.cost;
  }

  get availableDataCenters() {
    return this.package.locations
      .filter((location) => location.in_stock)
      .map((location) => ({
        value: location.key,
        label: `${location.value} (${location.key})`,
      }));
  }

  get availableOperatingSystem() {
    return [
      { value: "Install manually over IPMI" },
      { value: "Debian 12 (Bookworm)" },
      { value: "Debian 11 (Bullseye)" },
      { value: "Ubuntu 20.04 (Focal Fossa)" },
      { value: "Ubuntu 22.04 (Jammy Jellyfish)" },
      { value: "Ubuntu 24.04 (Noble Numbat)" },
      { value: "Windows Server Core 2022 Standard" },
      { value: "Windows Server 2022 Standard" },
      { value: "Windows Server Core 2025 Standard" },
      { value: "Windows Server 2025 Standard" },
    ];
  }

  get hasResellerPricing() {
    return this.abilities.cannot("access pricing for organization", this.current.organization);
  }

  get availableStorage() {
    if (this.package.storageAlternatives.length > 0) {
      return this.package.storageAlternatives.map((storage) => ({
        value: storage.value,
        label: this.hasResellerPricing ? `${storage.value} GB` : `${storage.label}`,
        cost: storage.cost,
      }));
    }
    return [{ value: this.package.storage, label: `${this.package.storage} GB`, cost: 0 }];
  }

  get availableConnection() {
    if (this.package.connectionAlternatives.length > 0) {
      return this.package.connectionAlternatives.map((bandwidth) => ({
        value: bandwidth.value,
        label: this.hasResellerPricing ? `${bandwidth.value} Gbps` : `${bandwidth.label}`,
        cost: bandwidth.cost,
      }));
    }
    return [{ value: this.package.connection, label: `${this.package.connection} Gbps`, cost: 0 }];
  }

  get isWindows() {
    return this.selectedOperatingSystem?.toLowerCase().startsWith("windows");
  }

  onSelectDataCenter = (event) => (this.selectedDataCenter = event.target.value);

  onSelectOperatingSystem = (event) => (this.selectedOperatingSystem = event.target.value);

  onSelectStorage = (event) =>
    (this.selectedStorage = this.availableStorage.find((storage) => storage.value === parseInt(event.target.value)));

  onSelectedConnection = (event) =>
    (this.selectedConnection = this.availableConnection.find(
      (bandwidth) => bandwidth.value === parseInt(event.target.value),
    ));

  submitTask = task(async () => {
    if (
      await this.task.perform(
        this.package,
        this.selectedDataCenter,
        this.selectedOperatingSystem,
        this.selectedStorage.value,
        this.selectedConnection.value,
        this.cost,
      )
    ) {
      this.modal.close();
    }
  });

  <template>
    <GModalHeader data-test-dedicated-server-order-form-header>
      {{t "component.modal.dedicated-server-order-form.header" name=this.package.name}}
    </GModalHeader>
    <GModalBody data-test-dedicated-server-order-form-body>
      <TaskForm id="dedicated-server-order-form" @task={{this.submitTask}}>
        <div class="grid gap-6 text-gray-800 mb-6" data-test-dedicated-server-order-intro>
          <p>{{t "component.modal.dedicated-server-order-form.info.delivery"}}</p>
          <p>{{t "component.modal.dedicated-server-order-form.info.email-confirmation"}}</p>
        </div>

        <div class="rounded-sm border border-gray-200 bg-gray-50 p-4" data-test-dedicated-server-order-summary>
          <p class="mb-4" data-test-dedicated-server-order-summary-name>
            {{t "component.modal.dedicated-server-order-form.package" name=this.package.name}}
          </p>
          <div class="grid text-sm">
            <div
              class="flex items-center justify-between border-b border-gray-200 py-2"
              data-test-dedicated-server-order-summary-cpu
            >
              <Label class="w-auto mb-0">{{t "label.cpu"}}</Label>
              <div>
                {{this.package.cpu.value}}
                <span class="text-gray-800">{{this.package.cpu.subValue}}</span>
              </div>
            </div>
            <div
              class="flex items-center justify-between border-b border-gray-200 py-2"
              data-test-dedicated-server-order-summary-memory
            >
              <Label class="w-auto mb-0">{{t "label.memory"}}</Label>
              <div>{{t "servers.create.dedicated.memory" memory=this.package.memory}}</div>
            </div>
            <div
              class="flex items-center justify-between border-b border-gray-200 py-2"
              data-test-dedicated-server-order-summary-storage
            >
              <Label class="w-auto mb-0">{{t "label.usable-storage"}}</Label>
              <div>{{t "servers.create.dedicated.storage" storage=this.package.storage}}</div>
            </div>
            <div
              class="flex items-center justify-between border-b border-gray-200 py-2"
              data-test-dedicated-server-order-summary-connection
            >
              <Label class="w-auto mb-0">{{t "label.connection"}}</Label>
              <div class="inline-flex items-center gap-1.5">
                {{t "servers.create.dedicated.connection" connection=this.package.connection}}
                {{#if this.package.commit}}
                  <InfoPopup data-test-dedicated-server-order-summary-connection-commit>
                    {{t
                      "servers.create.dedicated.commit"
                      commit=this.package.commit
                      connection=this.package.connection
                    }}
                  </InfoPopup>
                {{/if}}
              </div>
            </div>
            <div class="flex items-center justify-between py-2" data-test-dedicated-server-order-summary-ipv4>
              <Label class="w-auto mb-0">{{t "label.ipv4"}}</Label>
              <div>
                {{! eslint-disable ember/template-no-bare-strings }}
                1 pcs
                {{! eslint-enable ember/template-no-bare-strings }}
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-col gap-4 mt-6">
          <p data-test-dedicated-server-order-configuration>
            {{t "component.modal.dedicated-server-order-form.configuration"}}
          </p>
          <div>
            <Label for="data-center">{{t "label.data-center"}}</Label>
            <Select
              id="data-center"
              data-test-dedicated-server-order-configuration-data-center
              @items={{this.availableDataCenters}}
              @value={{this.selectedDataCenter}}
              {{on "change" this.onSelectDataCenter}}
            />
          </div>
          <div>
            <Label class="flex items-center gap-2" for="operating-system">{{t "label.operating-system"}}</Label>
            <Select
              id="operating-system"
              data-test-dedicated-server-order-configuration-operating-system
              @items={{this.availableOperatingSystem}}
              @value={{this.selectedOperatingSystem}}
              {{on "change" this.onSelectOperatingSystem}}
            />
          </div>
          {{#if this.package.storageAlternatives}}
            <div>
              <Label class="flex items-center gap-2" for="selected-storage">
                {{t "label.usable-storage"}}
                <InfoPopup>{{t "component.modal.dedicated-server-order-form.storage-popup"}}</InfoPopup>
              </Label>
              <Select
                id="selected-storage"
                data-test-dedicated-server-order-configuration-storage
                @items={{this.availableStorage}}
                @value={{this.selectedStorage.value}}
                {{on "change" this.onSelectStorage}}
              />
            </div>
          {{/if}}
          {{#if this.package.connectionAlternatives}}
            <div>
              <Label class="flex items-center gap-2" for="selected-connection">
                {{t "label.connection"}}
              </Label>
              <Select
                id="selected-connection"
                data-test-dedicated-server-order-configuration-connection
                @items={{this.availableConnection}}
                @value={{this.selectedConnection.value}}
                {{on "change" this.onSelectedConnection}}
              />
            </div>
          {{/if}}
        </div>
        <div
          class="grid gap-2 items-center justify-end mt-8 pt-4 border-t border-gray-200"
          data-test-dedicated-server-order-cost
        >
          <div class="flex gap-12 items-center justify-end" data-test-dedicated-server-order-cost-setup-fee>
            <Label class="mb-0">{{t "component.modal.dedicated-server-order-form.setup-fee"}}</Label>
            <CostCompact @price="0" @currency="SEK" @oneTimeCost={{true}} />
          </div>
          <div class="flex gap-12 items-center justify-end" data-test-dedicated-server-order-cost-mrc>
            <div class="flex gap-2 items-center">
              <Label class="mb-0">{{t "label.mrc"}}</Label>
              <InfoPopup>{{t "servers.create.dedicated.mrc-popup"}}</InfoPopup>
            </div>
            <CostCompact @price={{this.cost}} @currency="SEK" />
          </div>
        </div>
        {{#if this.isWindows}}
          <div class="flex justify-end pt-2">
            <AlertMessage>{{t "component.modal.dedicated-server-order-form.window-license-information"}}</AlertMessage>
          </div>
        {{/if}}
      </TaskForm>
    </GModalBody>
    <GModalFooter data-test-dedicated-server-order-form-footer>
      <GButton class="mr-2" data-test-dedicated-server-order-cancel @color="gray" {{on "click" (modalClose)}}>
        {{t "button.cancel"}}
      </GButton>

      <GButton data-test-dedicated-server-order-submit @type="submit" @color="green" form="dedicated-server-order-form">
        {{t "button.place-order"}}
      </GButton>
    </GModalFooter>
  </template>
}
